import { useCallback } from 'react';

import { useLocalStorageValue } from 'src/app/shared/hooks/storage';

import { SUGGESTED_TEMPLATE_NOTICE } from '../../../../constants';
import type {
	LocalStorageJsonValueHook,
	LocalStoragePetitionUpdateForm,
	LocalStoragePetitionUpdateMedia,
	PetitionUpdateFormValues,
	PetitionUpdateMedia,
} from '../../types';

function parse(str: string | null): unknown {
	if (!str) return {};
	try {
		return JSON.parse(str);
	} catch (e) {
		return {};
	}
}

function useLocalStorageJsonValue<DATA_TYPE>(key: string): LocalStorageJsonValueHook<DATA_TYPE> {
	const [jsonValue, setJsonValue] = useLocalStorageValue(key);
	const data = parse(jsonValue) as DATA_TYPE;
	const saveData = useCallback(
		(updatedData: DATA_TYPE) => {
			try {
				setJsonValue(JSON.stringify(updatedData));
			} catch {
				// Ignore failures setting data because the create form has no UI for reporting saved information.
			}
		},
		[setJsonValue],
	);
	const removeData = useCallback(() => localStorage.removeItem(key), [key]);

	return { data, saveData, removeData };
}

function localStorageKeyPostfix(petitionId: string, isTemplateFilled: boolean): string {
	return isTemplateFilled ? `${petitionId}_${SUGGESTED_TEMPLATE_NOTICE}` : petitionId;
}

export function useLocalStoragePetitionUpdateMedia(
	petitionId: string,
	isTemplateFilled: boolean,
): LocalStoragePetitionUpdateMedia {
	const postfix = localStorageKeyPostfix(petitionId, isTemplateFilled);
	const {
		data: petitionUpdateMedia,
		saveData: savePetitionUpdateMedia,
		removeData: removePetitionUpdateMedia,
	} = useLocalStorageJsonValue<PetitionUpdateMedia>(`petition_update_media_${postfix}`);
	return { petitionUpdateMedia, savePetitionUpdateMedia, removePetitionUpdateMedia };
}

export function useLocalStoragePetitionUpdateForm(
	petitionId: string,
	isTemplateFilled: boolean,
): LocalStoragePetitionUpdateForm {
	const postfix = localStorageKeyPostfix(petitionId, isTemplateFilled);
	const {
		data: petitionUpdateFormValues,
		saveData: savePetitionUpdateFormValues,
		removeData: removePetitionUpdateFormValues,
	} = useLocalStorageJsonValue<PetitionUpdateFormValues>(`petition_update_${postfix}`);
	return { petitionUpdateFormValues, savePetitionUpdateFormValues, removePetitionUpdateFormValues };
}
